import {
  ArrowsDownUp,
  CaretDown,
  CaretUp
} from "@phosphor-icons/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { twRing } from "@/lib/emotionTw";
import { TableEmptyState } from "./TableEmptyState";
import { TableErrorState } from "./TableErrorState";
import { TablePagination } from "./TablePagination";
import { TableSkeleton } from "./TableSkeleton";
import type { DataTableColumn, DataTableProps, DataTableSorting } from "./DataTable.types";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const defaultColumnWidth = 144;
const defaultLeadingColumnWidth = 220;
const selectionColumnWidth = 44;
const defaultRowActionsWidth = 146;
// Pinning is only offered on the first few columns — pinning a column deep in
// the scroll area would freeze everything before it and leave no room to scroll.
const maxPinnableColumns = 3;

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, in the original class-list emission order. The named-group row
 * hover (`group-hover/row:bg-surface-app`) is expressed as `&:hover > td` on
 * the row — it matches the identical element set with winning specificity —
 * and the header pin-button reveal (`group-hover:opacity-100`) as
 * `&:hover button[title]` on the header cell (the pin button is the only
 * titled button the header renders). Verified against the frozen baseline. */

const headerMetrics: CSSObject = {
  height: "38px",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "7px",
  paddingBottom: "7px"
};

const cellMetrics: CSSObject = {
  height: "4rem",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
};

const headerTextStyles: CSSObject = {
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: ".24px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
};

const pinEdgeShadow: CSSObject = {
  "--tw-shadow": "3px 0 8px rgba(0,0,0,.05)",
  "--tw-shadow-colored": "3px 0 8px var(--tw-shadow-color)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
};

const actionsHeaderShadow: CSSObject = {
  "--tw-shadow": "0px 2px 6px rgba(0,0,0,.08)",
  "--tw-shadow-colored": "0px 2px 6px var(--tw-shadow-color)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
};

const actionsCellShadow: CSSObject = {
  "--tw-shadow": "-3px 0 4px rgba(0,0,0,.04)",
  "--tw-shadow-colored": "-3px 0 4px var(--tw-shadow-color)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
};

const surfaceApp: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
};

const DataTableRoot = styled("div", {
  label: "AgenticDataTable-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { fill: boolean } }>(({ ownerState }) => ({
  display: "flex",
  minWidth: "0px",
  minHeight: "0px",
  flex: ownerState.fill ? "1 1 auto" : "var(--bfrs-data-table-flex, 0 1 auto)",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-md)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
}));

const DataTableScroller = styled("div", {
  label: "AgenticDataTable-scroller"
})({
  minHeight: "0px",
  flex: "1 1 auto",
  overflow: "auto"
});

const DataTableTable = styled("table", {
  label: "AgenticDataTable-table"
})({
  width: "100%",
  borderCollapse: "separate",
  "--tw-border-spacing-x": "0px",
  "--tw-border-spacing-y": "0px",
  borderSpacing: "var(--tw-border-spacing-x) var(--tw-border-spacing-y)",
  textAlign: "left"
});

const DataTableHead = styled("thead", {
  label: "AgenticDataTable-head",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { sticky: boolean } }>(({ ownerState }) => [
  ownerState.sticky && {
    position: "sticky",
    top: 0,
    zIndex: 30
  }
]);

const DataTableHeaderRow = styled("tr", {
  label: "AgenticDataTable-headerRow"
})(surfaceApp);

type HeaderCellKind = "selection" | "leading" | "data" | "actions";

interface HeaderCellOwnerState {
  kind: HeaderCellKind;
  sortable?: boolean;
  sorted?: boolean;
  reorderable?: boolean;
  dropTarget?: boolean;
  edgeShadow?: boolean;
  actionsShadow?: boolean;
}

const headerCellBase: CSSObject = {
  // Positioning context so the pin button can float without reserving header width.
  position: "relative",
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
  ...surfaceApp
};

const DataTableHeaderCell = styled("th", {
  label: "AgenticDataTable-headerCell",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: HeaderCellOwnerState }>(({ ownerState }) => [
  headerCellBase,
  ownerState.kind === "selection" && {
    textAlign: "center",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  ownerState.kind === "leading" && {
    verticalAlign: "middle",
    ...headerTextStyles,
    "&:hover button[title]": { opacity: 1 }
  },
  ownerState.kind === "data" && {
    borderLeftWidth: "2px",
    borderLeftColor: "transparent",
    ...headerTextStyles,
    "&:hover button[title]": { opacity: 1 }
  },
  ownerState.kind === "actions" && headerTextStyles,
  headerMetrics,
  ownerState.sortable && {
    "&:hover": {
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
    }
  },
  ownerState.sorted && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.reorderable && {
    cursor: "grab",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    userSelect: "none",
    "&:active": { cursor: "grabbing" }
  },
  ownerState.dropTarget && {
    "--tw-border-opacity": "1",
    borderLeftColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))"
  },
  ownerState.edgeShadow && pinEdgeShadow,
  ownerState.actionsShadow && actionsHeaderShadow
]);

const DataTableHeaderContent = styled("span", {
  label: "AgenticDataTable-headerContent",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { align?: "left" | "right" | "center"; wide?: boolean } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    maxWidth: "100%",
    alignItems: "center",
    gap: ownerState.wide ? ".5rem" : ".375rem"
  },
  ownerState.align === "right" && { justifyContent: "flex-end" },
  ownerState.align === "center" && { justifyContent: "center" }
]);

const DataTableSortButton = styled("button", {
  label: "AgenticDataTable-sortButton"
})({
  display: "inline-flex",
  minWidth: "0px",
  alignItems: "center",
  gap: ".25rem",
  borderRadius: "var(--bfrs-radius-sm)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const DataTableTruncate = styled("span", {
  label: "AgenticDataTable-truncate",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { overflow: "truncate" | "visible" } }>(({ ownerState }) => ({
  minWidth: "0px",
  overflow: ownerState.overflow === "visible" ? "visible" : "hidden",
  textOverflow: ownerState.overflow === "visible" ? "clip" : "ellipsis",
  whiteSpace: "nowrap"
}));

const DataTableSortIdleIcon = styled(ArrowsDownUp, {
  label: "AgenticDataTable-sortIdleIcon"
})({
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
});

const DataTablePinButton = styled("button", {
  label: "AgenticDataTable-pinButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { pinned: boolean; side: "left" | "right" } }>(({ ownerState }) => [
  {
    // Floated (not a flex item) so the header label keeps its full width; the pin
    // overlays the cell edge on hover instead of shortening the text box and
    // forcing a premature ellipsis.
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    display: "inline-flex",
    height: "17px",
    width: "2.5rem",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".375rem",
    paddingRight: ".375rem",
    fontSize: "9px",
    fontWeight: 700,
    textTransform: "none",
    lineHeight: "1.5",
    letterSpacing: "0em",
    opacity: 0,
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:focus-visible": {
      opacity: 1,
      outline: "2px solid transparent",
      outlineOffset: "2px",
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
      "--tw-ring-opacity": "1"
    }
  },
  // Anchor to the far edge of the header cell (opposite the text flush side).
  ownerState.side === "right" ? { right: "1.25rem" } : { left: "1.25rem" },
  ownerState.pinned
    ? {
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-success-soft) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-success-contrast) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-raised) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
        "&:hover": {
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
        }
      }
]);

interface BodyRowOwnerState {
  selected: boolean;
  clickable: boolean;
}

const DataTableBodyRow = styled("tr", {
  label: "AgenticDataTable-row",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: BodyRowOwnerState }>(({ ownerState }) => [
  {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": surfaceApp,
    "&:hover > td": surfaceApp
  },
  ownerState.selected && surfaceApp,
  ownerState.clickable && { cursor: "pointer" }
]);

type BodyCellKind = "selection" | "leading" | "data" | "actions";

interface BodyCellOwnerState {
  kind: BodyCellKind;
  selected: boolean;
  cellTextSize: "default" | "compact";
  edgeShadow?: boolean;
  actionsShadow?: boolean;
}

const DataTableCell = styled("td", {
  label: "AgenticDataTable-cell",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: BodyCellOwnerState }>(({ ownerState }) => [
  {
    borderBottomWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    verticalAlign: "middle"
  },
  ownerState.kind === "data" && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  cellMetrics,
  ownerState.selected && surfaceApp,
  ownerState.kind === "data" && { fontSize: "12px" },
  ownerState.cellTextSize === "compact" && {
    fontSize: "12px",
    "& *": {
      fontSize: "inherit"
    }
  },
  ownerState.edgeShadow && pinEdgeShadow,
  ownerState.actionsShadow && actionsCellShadow
]);

const DataTableCheckbox = styled("input", {
  label: "AgenticDataTable-checkbox"
})({
  width: ".75rem",
  height: ".75rem",
  flexShrink: 0,
  cursor: "pointer",
  accentColor: "hsl(var(--bfrs-color-primary) / 1)"
});

const DataTableCheckboxCenter = styled("div", {
  label: "AgenticDataTable-checkboxCenter"
})({
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});

const DataTableLeadingLayout = styled("div", {
  label: "AgenticDataTable-leadingLayout"
})({
  display: "flex",
  alignItems: "center",
  gap: ".75rem"
});

const DataTableLeadingContent = styled("div", {
  label: "AgenticDataTable-leadingContent"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

function getValue<Row>(row: Row, column: DataTableColumn<Row>) {
  if (column.accessorFn) return column.accessorFn(row);
  if (column.accessorKey) return row[column.accessorKey];
  return undefined;
}

function getSortValue<Row>(row: Row, column: DataTableColumn<Row>) {
  if (column.sortValue) return column.sortValue(row);
  return getValue(row, column);
}

function compareValues(left: unknown, right: unknown) {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left ?? "").localeCompare(String(right ?? ""), undefined, { numeric: true, sensitivity: "base" });
}

function toPixelWidth(width: number | string | undefined, fallback: number) {
  if (typeof width === "number") return width;
  if (typeof width === "string" && width.endsWith("px")) {
    const parsed = Number.parseFloat(width);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function syncColumnOrder(current: string[], columnIds: string[]) {
  const known = current.filter((id) => columnIds.includes(id));
  const missing = columnIds.filter((id) => !known.includes(id));
  return [...known, ...missing];
}

function moveItem(items: string[], draggedId: string, targetId: string) {
  const next = [...items];
  const from = next.indexOf(draggedId);
  const to = next.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return items;
  next.splice(from, 1);
  next.splice(to, 0, draggedId);
  return next;
}

type MeasuredTableWidths = {
  selection: number;
  leading: number;
  columns: number[];
  rowActions: number;
};

function roundMeasuredWidth(width: number) {
  return Math.round(width * 100) / 100;
}

function measuredOrFallback(width: number | undefined, fallback: number) {
  return width && width > 0 ? width : fallback;
}

function sameMeasuredWidths(current: MeasuredTableWidths | null, next: MeasuredTableWidths) {
  if (!current) return false;
  if (current.columns.length !== next.columns.length) return false;
  if (
    Math.abs(current.selection - next.selection) > 0.5 ||
    Math.abs(current.leading - next.leading) > 0.5 ||
    Math.abs(current.rowActions - next.rowActions) > 0.5
  ) {
    return false;
  }
  return current.columns.every((width, index) => Math.abs(width - next.columns[index]) <= 0.5);
}

export function DataTable<Row>({
  data,
  columns,
  getRowId,
  loading,
  error,
  emptyState,
  // Default no-data view is a two-line message (title + description). Consumers
  // can override either line, or replace the whole thing via `emptyState`.
  emptyTitle = "No records found",
  emptyDescription = "There's nothing to show here yet.",
  pagination,
  enableSorting = true,
  sorting,
  defaultSorting = null,
  onSortingChange,
  visibleColumnIds,
  columnOrder,
  defaultColumnOrder,
  onColumnOrderChange,
  enableColumnPinning = true,
  pinnedColumnCount,
  defaultPinnedColumnCount = 0,
  onPinnedColumnCountChange,
  pinLeadingColumn,
  defaultPinLeadingColumn = false,
  onPinLeadingColumnChange,
  enableColumnReordering = true,
  resetKey,
  showSortIcon = "always",
  leadingColumn,
  rowActions,
  rowActionsHeader = "Actions",
  rowActionsWidth = defaultRowActionsWidth,
  stickyRowActions = Boolean(rowActions),
  selection,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  stickyHeader = true,
  fill = false,
  headerTextOverflow = "truncate",
  cellTextSize = "default",
  minWidth = "100%",
  maxHeight,
  className,
  scrollerClassName,
  tableClassName,
  headerClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  "aria-label": ariaLabel = "Data table"
}: DataTableProps<Row>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [internalSorting, setInternalSorting] = useState<DataTableSorting | null>(defaultSorting);
  const [internalColumnOrder, setInternalColumnOrder] = useState(() => defaultColumnOrder ?? columns.map((column) => column.id));
  const [internalPinnedColumnCount, setInternalPinnedColumnCount] = useState(defaultPinnedColumnCount);
  const [internalPinLeadingColumn, setInternalPinLeadingColumn] = useState(defaultPinLeadingColumn);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [measuredWidths, setMeasuredWidths] = useState<MeasuredTableWidths | null>(null);

  const activeSorting = enableSorting ? (sorting === undefined ? internalSorting : sorting) : null;
  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const columnById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);

  useEffect(() => {
    if (columnOrder !== undefined) return;
    setInternalColumnOrder((current) => syncColumnOrder(current, columnIds));
  }, [columnIds, columnOrder]);

  useEffect(() => {
    if (resetKey === undefined) return;
    if (sorting === undefined) setInternalSorting(defaultSorting);
    if (columnOrder === undefined) setInternalColumnOrder(syncColumnOrder(defaultColumnOrder ?? columnIds, columnIds));
    if (pinnedColumnCount === undefined) setInternalPinnedColumnCount(defaultPinnedColumnCount);
    if (pinLeadingColumn === undefined) setInternalPinLeadingColumn(defaultPinLeadingColumn);
    setDragIndex(null);
    setDropIndex(null);
  }, [
    columnIds,
    columnOrder,
    defaultColumnOrder,
    defaultPinnedColumnCount,
    defaultPinLeadingColumn,
    defaultSorting,
    pinLeadingColumn,
    pinnedColumnCount,
    resetKey,
    sorting
  ]);

  const orderedColumns = useMemo(() => {
    const orderedIds = syncColumnOrder(columnOrder ?? internalColumnOrder, columnIds);
    return orderedIds
      .map((id) => columnById.get(id))
      .filter((column): column is DataTableColumn<Row> => Boolean(column));
  }, [columnById, columnIds, columnOrder, internalColumnOrder]);

  const displayColumns = useMemo(() => {
    if (!visibleColumnIds) return orderedColumns;
    const visibleIds = new Set(visibleColumnIds);
    return orderedColumns.filter((column) => visibleIds.has(column.id));
  }, [orderedColumns, visibleColumnIds]);

  const declaredDisplayColumnWidths = useMemo(
    () => displayColumns.map((column) => toPixelWidth(column.width ?? column.minWidth, defaultColumnWidth)),
    [displayColumns]
  );

  const activePinnedColumnCount = enableColumnPinning
    ? Math.min(
        displayColumns.length,
        Math.max(0, pinnedColumnCount === undefined ? internalPinnedColumnCount : pinnedColumnCount)
      )
    : 0;
  const activePinLeadingColumn = enableColumnPinning && leadingColumn
    ? pinLeadingColumn === undefined ? internalPinLeadingColumn : pinLeadingColumn
    : false;
  // The leading column sits to the left of every data column, so it must stay
  // pinned whenever any data column is pinned — otherwise it scrolls away and
  // leaves the pinned data columns detached from the row's identity column.
  const leadingColumnIsPinned = Boolean(leadingColumn) && (activePinLeadingColumn || activePinnedColumnCount > 0);
  // The edge shadow marks the right boundary of the pinned block, so it belongs
  // only to the rightmost pinned column. The leading column is rightmost only
  // when no data columns are pinned to its right.
  const leadingColumnShowsEdgeShadow = leadingColumnIsPinned && activePinnedColumnCount === 0;
  const hasStandaloneSelection = Boolean(selection && !leadingColumn);
  const selectionIsSticky = hasStandaloneSelection && activePinnedColumnCount > 0;
  const leadingColumnWidth = toPixelWidth(leadingColumn?.width ?? leadingColumn?.minWidth, defaultLeadingColumnWidth);
  const measuredDisplayColumnWidths = measuredWidths?.columns.length === displayColumns.length ? measuredWidths.columns : undefined;
  const stickySelectionWidth = measuredOrFallback(measuredWidths?.selection, selectionColumnWidth);
  const stickyLeadingColumnWidth = measuredOrFallback(measuredWidths?.leading, leadingColumnWidth);
  const baseStickyLeft = (selectionIsSticky ? stickySelectionWidth : 0) + (leadingColumnIsPinned ? stickyLeadingColumnWidth : 0);
  const visibleColumnCount = displayColumns.length + (hasStandaloneSelection ? 1 : 0) + (leadingColumn ? 1 : 0) + (rowActions ? 1 : 0);

  const rowId = (row: Row, index: number) => getRowId?.(row, index) ?? String((row as { id?: string | number }).id ?? index);

  const measureTableWidths = useCallback(() => {
    const headerRow = tableRef.current?.tHead?.rows[0];
    if (!headerRow) return;

    const cells = Array.from(headerRow.cells);
    let cellIndex = 0;
    const readCellWidth = () => roundMeasuredWidth(cells[cellIndex++]?.getBoundingClientRect().width ?? 0);
    const nextWidths: MeasuredTableWidths = {
      selection: hasStandaloneSelection ? readCellWidth() : 0,
      leading: leadingColumn ? readCellWidth() : 0,
      columns: displayColumns.map(() => readCellWidth()),
      rowActions: rowActions ? readCellWidth() : 0
    };

    setMeasuredWidths((current) => sameMeasuredWidths(current, nextWidths) ? current : nextWidths);
  }, [displayColumns, hasStandaloneSelection, leadingColumn, rowActions]);

  useIsomorphicLayoutEffect(() => {
    measureTableWidths();
  });

  useIsomorphicLayoutEffect(() => {
    const table = tableRef.current;
    if (!table || typeof window === "undefined" || typeof window.ResizeObserver === "undefined") return undefined;

    const observer = new window.ResizeObserver(measureTableWidths);
    observer.observe(table);
    Array.from(table.tHead?.rows[0]?.cells ?? []).forEach((cell) => observer.observe(cell));

    return () => observer.disconnect();
  }, [measureTableWidths]);

  const sortedData = useMemo(() => {
    if (!activeSorting) return data;
    const column = columns.find((item) => item.id === activeSorting.columnId);
    if (!column) return data;
    return [...data].sort((left, right) => {
      const result = compareValues(getSortValue(left, column), getSortValue(right, column));
      return activeSorting.direction === "asc" ? result : -result;
    });
  }, [activeSorting, columns, data]);

  const allSelected = sortedData.length > 0 && sortedData.every((row, index) => selectedRowIds.includes(rowId(row, index)));

  const updateSorting = (column: DataTableColumn<Row>) => {
    if (!enableSorting || !column.sortable) return;
    const next: DataTableSorting =
      activeSorting?.columnId === column.id
        ? { columnId: column.id, direction: activeSorting.direction === "asc" ? "desc" : "asc" }
        : { columnId: column.id, direction: "asc" };
    if (sorting === undefined) setInternalSorting(next);
    onSortingChange?.(next);
  };

  const updateColumnOrder = (next: string[]) => {
    if (columnOrder === undefined) setInternalColumnOrder(next);
    onColumnOrderChange?.(next);
  };

  const updatePinnedColumnCount = (count: number) => {
    const next = Math.min(displayColumns.length, Math.max(0, count));
    if (pinnedColumnCount === undefined) setInternalPinnedColumnCount(next);
    onPinnedColumnCountChange?.(next);
  };

  const updatePinLeadingColumn = (pinned: boolean) => {
    if (pinLeadingColumn === undefined) setInternalPinLeadingColumn(pinned);
    onPinLeadingColumnChange?.(pinned);
  };

  const toggleAllRows = () => {
    if (!onSelectedRowIdsChange) return;
    onSelectedRowIdsChange(allSelected ? [] : sortedData.map((row, index) => rowId(row, index)));
  };

  const toggleRow = (id: string) => {
    if (!onSelectedRowIdsChange) return;
    onSelectedRowIdsChange(selectedRowIds.includes(id) ? selectedRowIds.filter((selectedId) => selectedId !== id) : [...selectedRowIds, id]);
  };

  const getStickyLeft = (idx: number) => {
    let left = baseStickyLeft;
    for (let i = 0; i < idx; i += 1) {
      left += measuredOrFallback(measuredDisplayColumnWidths?.[i], declaredDisplayColumnWidths[i] ?? defaultColumnWidth);
    }
    return left;
  };

  const handleDragStart = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    setDragIndex(idx);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    event.preventDefault();
    setDropIndex(idx);
  };

  const handleDrop = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    event.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }

    const draggedId = displayColumns[dragIndex]?.id;
    const targetId = displayColumns[idx]?.id;
    if (draggedId && targetId) {
      updateColumnOrder(moveItem(columnOrder ?? internalColumnOrder, draggedId, targetId));
      if (dragIndex < activePinnedColumnCount) updatePinnedColumnCount(Math.min(activePinnedColumnCount, idx + 1));
    }
    setDragIndex(null);
    setDropIndex(null);
  };

  const stickyHeaderCell = (left: number, zIndex = 20): CSSProperties => ({
    position: "sticky",
    left,
    ...(stickyHeader ? { top: 0 } : {}),
    zIndex
  });

  const stickyBodyCell = (left: number, zIndex = 10): CSSProperties => ({
    position: "sticky",
    left,
    zIndex
  });

  const stickyActionStyle: CSSProperties = stickyRowActions
    ? {
        position: "sticky",
        right: 0,
        zIndex: 12
      }
    : {};
  const stickyActionHeaderStyle: CSSProperties = stickyRowActions
    ? {
        position: "sticky",
        right: 0,
        ...(stickyHeader ? { top: 0 } : {}),
        zIndex: 24
      }
    : {};

  return (
    <DataTableRoot className={className} ownerState={{ fill }}>
      <DataTableScroller className={scrollerClassName} style={maxHeight !== undefined ? { maxHeight } : undefined}>
        <DataTableTable ref={tableRef} aria-label={ariaLabel} aria-busy={loading || undefined} className={tableClassName} style={{ minWidth, tableLayout: "fixed" }}>
          <DataTableHead ownerState={{ sticky: Boolean(stickyHeader) }} className={headerClassName}>
            <DataTableHeaderRow>
              {hasStandaloneSelection && (
                <DataTableHeaderCell
                  ownerState={{ kind: "selection", edgeShadow: selectionIsSticky }}
                  style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth, ...(selectionIsSticky ? stickyHeaderCell(0, 24) : {}) }}
                >
                  <DataTableCheckboxCenter>
                    <DataTableCheckbox type="checkbox" checked={allSelected} onChange={toggleAllRows} aria-label="Select all rows" />
                  </DataTableCheckboxCenter>
                </DataTableHeaderCell>
              )}

              {leadingColumn && (
                <DataTableHeaderCell
                  scope="col"
                  ownerState={{ kind: "leading", edgeShadow: leadingColumnShowsEdgeShadow }}
                  className={leadingColumn.headerClassName}
                  style={{
                    width: leadingColumn.width,
                    minWidth: leadingColumn.minWidth ?? leadingColumn.width ?? defaultLeadingColumnWidth,
                    ...(leadingColumnIsPinned ? stickyHeaderCell(selectionIsSticky ? selectionColumnWidth : 0, 23) : {}),
                    ...leadingColumn.headerStyle
                  }}
                >
                  <DataTableHeaderContent ownerState={{ wide: true }}>
                    {selection && (
                      <DataTableCheckbox type="checkbox" checked={allSelected} onChange={toggleAllRows} aria-label="Select all rows" />
                    )}
                    <DataTableTruncate ownerState={{ overflow: headerTextOverflow }}>{leadingColumn.header}</DataTableTruncate>
                    {enableColumnPinning && (
                      <DataTablePinButton
                        type="button"
                        ownerState={{ pinned: leadingColumnIsPinned, side: "right" }}
                        title={leadingColumnIsPinned ? "Unpin leading column" : "Pin leading column"}
                        aria-label={leadingColumnIsPinned ? "Unpin leading column" : "Pin leading column"}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (leadingColumnIsPinned) {
                            updatePinLeadingColumn(false);
                            updatePinnedColumnCount(0);
                          } else {
                            updatePinLeadingColumn(true);
                          }
                        }}
                      >
                        {leadingColumnIsPinned ? "Unpin" : "Pin"}
                      </DataTablePinButton>
                    )}
                  </DataTableHeaderContent>
                </DataTableHeaderCell>
              )}

              {displayColumns.map((column, idx) => {
                const sorted = activeSorting?.columnId === column.id;
                const pinned = idx < activePinnedColumnCount;
                const lastPinned = idx === activePinnedColumnCount - 1;
                const headerStyle: CSSProperties = {
                  width: column.width,
                  minWidth: column.minWidth ?? column.width,
                  textAlign: column.align,
                  ...(pinned ? stickyHeaderCell(getStickyLeft(idx), 22) : {}),
                  ...column.headerStyle
                };
                const columnSortable = enableSorting && column.sortable;
                const showInactiveSortIcon = columnSortable && showSortIcon === "always" && !sorted;
                const showActiveSortIcon = columnSortable && sorted && showSortIcon !== "never";
                const pinButton = enableColumnPinning && idx < maxPinnableColumns && (
                  <DataTablePinButton
                    type="button"
                    ownerState={{ pinned, side: column.align === "right" ? "left" : "right" }}
                    title={pinned && lastPinned ? "Unpin columns" : "Pin up to this column"}
                    aria-label={pinned && lastPinned ? "Unpin columns" : `Pin up to ${String(column.header)}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      updatePinnedColumnCount(pinned && lastPinned ? 0 : idx + 1);
                    }}
                  >
                    {pinned ? "Unpin" : "Pin"}
                  </DataTablePinButton>
                );

                return (
                  <DataTableHeaderCell
                    key={column.id}
                    scope="col"
                    aria-sort={columnSortable ? (sorted ? (activeSorting.direction === "asc" ? "ascending" : "descending") : "none") : undefined}
                    draggable={enableColumnReordering}
                    onDragStart={(event) => handleDragStart(event, idx)}
                    onDragOver={(event) => handleDragOver(event, idx)}
                    onDrop={(event) => handleDrop(event, idx)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDropIndex(null);
                    }}
                    ownerState={{
                      kind: "data",
                      sortable: Boolean(columnSortable),
                      sorted: Boolean(sorted),
                      reorderable: enableColumnReordering,
                      dropTarget: dropIndex === idx,
                      edgeShadow: pinned && lastPinned
                    }}
                    className={column.headerClassName}
                    style={headerStyle}
                  >
                    <DataTableHeaderContent ownerState={{ align: column.align }}>
                      {/* For right-aligned columns render the pin button first so the label stays flush at the right edge, matching data cell text-align: right */}
                      {column.align === "right" && pinButton}
                      {columnSortable ? (
                        <DataTableSortButton type="button" onClick={() => updateSorting(column)}>
                          <DataTableTruncate ownerState={{ overflow: headerTextOverflow }}>{column.header}</DataTableTruncate>
                          {showActiveSortIcon && (activeSorting.direction === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
                          {showInactiveSortIcon && <DataTableSortIdleIcon size={12} />}
                        </DataTableSortButton>
                      ) : (
                        <DataTableTruncate ownerState={{ overflow: headerTextOverflow }}>{column.header}</DataTableTruncate>
                      )}
                      {column.align !== "right" && pinButton}
                    </DataTableHeaderContent>
                  </DataTableHeaderCell>
                );
              })}

              {rowActions && (
                <DataTableHeaderCell
                  ownerState={{ kind: "actions", actionsShadow: stickyRowActions }}
                  style={{ width: rowActionsWidth, minWidth: rowActionsWidth, ...stickyActionHeaderStyle }}
                >
                  {rowActionsHeader}
                </DataTableHeaderCell>
              )}
            </DataTableHeaderRow>
          </DataTableHead>
          <tbody>
            {loading ? (
              <TableSkeleton rows={5} columns={visibleColumnCount} />
            ) : error ? (
              <TableErrorState description={error} colSpan={visibleColumnCount} />
            ) : sortedData.length === 0 ? (
              emptyState ? (
                <tr><td colSpan={visibleColumnCount}>{emptyState}</td></tr>
              ) : (
                <TableEmptyState title={emptyTitle} description={emptyDescription} colSpan={visibleColumnCount} />
              )
            ) : (
              sortedData.map((row, rowIndex) => {
                const id = rowId(row, rowIndex);
                const selected = selectedRowIds.includes(id);
                const hovered = hoveredRowId === id;
                const resolvedRowClassName = typeof rowClassName === "function" ? rowClassName(row, rowIndex) : rowClassName;
                const rowState = { rowIndex, selected, hovered };

                return (
                  <DataTableBodyRow
                    key={id}
                    ownerState={{ selected, clickable: Boolean(onRowClick) }}
                    className={resolvedRowClassName}
                    onMouseEnter={() => setHoveredRowId(id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onClick={() => onRowClick?.(row)}
                  >
                    {hasStandaloneSelection && (
                      <DataTableCell
                        ownerState={{ kind: "selection", selected, cellTextSize, edgeShadow: selectionIsSticky }}
                        style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth, ...(selectionIsSticky ? stickyBodyCell(0, 14) : {}) }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <DataTableCheckboxCenter>
                          <DataTableCheckbox type="checkbox" checked={selected} onChange={() => toggleRow(id)} aria-label={`Select row ${rowIndex + 1}`} />
                        </DataTableCheckboxCenter>
                      </DataTableCell>
                    )}

                    {leadingColumn && (
                      <DataTableCell
                        ownerState={{ kind: "leading", selected, cellTextSize, edgeShadow: leadingColumnShowsEdgeShadow }}
                        className={leadingColumn.className}
                        style={{
                          width: leadingColumn.width,
                          minWidth: leadingColumn.minWidth ?? leadingColumn.width ?? defaultLeadingColumnWidth,
                          ...(leadingColumnIsPinned ? stickyBodyCell(selectionIsSticky ? selectionColumnWidth : 0, 13) : {}),
                          ...leadingColumn.style
                        }}
                        onClick={selection ? (event) => event.stopPropagation() : undefined}
                      >
                        <DataTableLeadingLayout>
                          {selection && (
                            <DataTableCheckbox type="checkbox" checked={selected} onChange={() => toggleRow(id)} aria-label={`Select row ${rowIndex + 1}`} />
                          )}
                          <DataTableLeadingContent>{leadingColumn.cell(row, rowState)}</DataTableLeadingContent>
                        </DataTableLeadingLayout>
                      </DataTableCell>
                    )}

                    {displayColumns.map((column, idx) => {
                      const value = getValue(row, column);
                      const pinned = idx < activePinnedColumnCount;
                      const lastPinned = idx === activePinnedColumnCount - 1;
                      const resolvedCellClassName =
                        typeof cellClassName === "function" ? cellClassName(row, rowIndex, column) : cellClassName;
                      return (
                        <DataTableCell
                          key={column.id}
                          ownerState={{ kind: "data", selected, cellTextSize, edgeShadow: pinned && lastPinned }}
                          className={clsx(resolvedCellClassName, column.className)}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth ?? column.width,
                            textAlign: column.align,
                            ...(pinned ? stickyBodyCell(getStickyLeft(idx), 12) : {}),
                            ...column.style
                          }}
                        >
                          {column.cell ? column.cell(row, { value, rowIndex, selected, hovered }) : String(value ?? "")}
                        </DataTableCell>
                      );
                    })}

                    {rowActions && (
                      <DataTableCell
                        ownerState={{ kind: "actions", selected, cellTextSize, actionsShadow: stickyRowActions }}
                        style={{ width: rowActionsWidth, minWidth: rowActionsWidth, ...stickyActionStyle }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {rowActions(row)}
                      </DataTableCell>
                    )}
                  </DataTableBodyRow>
                );
              })
            )}
          </tbody>
        </DataTableTable>
      </DataTableScroller>
      {pagination && <TablePagination {...pagination} />}
    </DataTableRoot>
  );
}
